'use client';

import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { SearchSuggestionsPreview } from './SearchSuggestionsPreview';
import type { SearchComboboxItem } from './types';

type SearchSuggestionsDropdownProps = {
  activeIndex: number;
  anchorRef: RefObject<HTMLDivElement | null>;
  emptyStateLabel: string;
  items: readonly SearchComboboxItem[];
  /** Si es 'inline' renderiza en el flujo (sin portal) — útil dentro de sheets controlados. */
  placement?: 'portal' | 'inline';
  previewId: string;
  optionIdPrefix?: string;
  showEmptyState: boolean;
  onSelect: (item: SearchComboboxItem) => void;
};

type AnchorRect = { left: number; top: number; width: number };

const GAP_FROM_INPUT = 0;

/**
 * Renderiza el preview de sugerencias. En modo `portal` (default) lo monta en
 * `document.body` con `position: fixed` atado al rect del input para escapar
 * de cualquier `overflow: hidden`. En modo `inline` lo renderiza junto al input
 * (útil dentro de un sheet/drawer controlado).
 */
export function SearchSuggestionsDropdown({
  activeIndex,
  anchorRef,
  emptyStateLabel,
  items,
  placement = 'portal',
  previewId,
  optionIdPrefix,
  showEmptyState,
  onSelect,
}: SearchSuggestionsDropdownProps) {
  const hasItems = items.length > 0;
  const shouldRender = hasItems || showEmptyState;

  const preview = shouldRender ? (
    <SearchSuggestionsPreview
      activeIndex={activeIndex}
      emptyStateLabel={emptyStateLabel}
      items={hasItems ? items : []}
      previewId={previewId}
      optionIdPrefix={optionIdPrefix}
      onPreviewSelect={onSelect}
    />
  ) : null;

  const [rect, setRect] = useState<AnchorRect | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (placement !== 'portal') return;
    if (!anchorRef.current) return;

    const updateRect = () => {
      const node = anchorRef.current;
      if (!node) return;
      const next = node.getBoundingClientRect();
      setRect({ left: next.left, top: next.bottom, width: next.width });
    };

    updateRect();

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateRect) : null;
    if (observer && anchorRef.current) observer.observe(anchorRef.current);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      observer?.disconnect();
    };
  }, [anchorRef, items.length, showEmptyState, placement]);

  if (!shouldRender) return null;

  if (placement === 'inline') {
    return preview;
  }

  if (!mounted || !rect) return null;

  return createPortal(
    <div
      className="adipa-search-bar-dropdown z-1000"
      style={{
        position: 'fixed',
        left: rect.left,
        top: rect.top + GAP_FROM_INPUT,
        width: rect.width,
      }}
    >
      {preview}
    </div>,
    document.body,
  );
}
